import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  GitCompare, Building2, Users2, Mail, Phone, Trophy, Star,
  Loader2, Crown, Sparkles, X,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const Stat = ({ icon: Icon, value, label, approx, best, testid }) => (
  <div className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 ${best ? 'border-emerald-500/40 bg-emerald-500/10' : 'border-border bg-background/60'}`} data-testid={testid}>
    <Icon className={`w-4 h-4 shrink-0 ${best ? 'text-emerald-500' : 'text-muted-foreground'}`} />
    <div className="min-w-0">
      <p className={`text-sm font-semibold leading-none ${best ? 'text-emerald-600' : 'text-foreground'}`}>
        {value == null ? '—' : value.toLocaleString()}{approx && value != null ? '≈' : ''}
      </p>
      <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{label}</p>
    </div>
  </div>
);

const ChipList = ({ label, items, sharedSet }) => {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">{label}</p>
      <div className="flex flex-wrap gap-1">
        {items.map((it, i) => {
          const shared = sharedSet && sharedSet.has(String(it).trim().toLowerCase());
          return (
            <span key={i}
              className={`text-[11px] px-1.5 py-0.5 rounded-full border ${shared ? 'bg-sky-500/15 text-sky-600 border-sky-500/30 font-medium' : 'bg-muted/50 text-foreground/80 border-border'}`}>
              {it}
            </span>
          );
        })}
      </div>
    </div>
  );
};

// Segment Compare — pick two targeting segments and see their live reach
// side-by-side, the targeting they share, who wins on contactable reach, and
// promote the winner to primary in one tap.
export const ICPSegmentCompare = ({ onChanged, refreshKey }) => {
  const [segments, setSegments] = useState([]);
  const [open, setOpen] = useState(false);
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [promoting, setPromoting] = useState(null);

  const loadSegments = useCallback(async () => {
    try {
      const r = await api.get(`${API}/api/brain/icp/segments`);
      const segs = r.data?.segments || [];
      setSegments(segs);
      if (segs.length >= 2) {
        setA((prev) => prev || segs[0].id);
        setB((prev) => prev || segs[1].id);
      }
    } catch { setSegments([]); }
  }, []);

  useEffect(() => { loadSegments(); }, [loadSegments, refreshKey]);

  const runCompare = useCallback(async () => {
    if (!a || !b || a === b) { setResult(null); return; }
    setLoading(true);
    try {
      const r = await api.post(`${API}/api/brain/icp/segments/compare`, { segment_ids: [a, b] });
      setResult(r.data || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not compare segments');
      setResult(null);
    } finally { setLoading(false); }
  }, [a, b]);

  // Re-run the comparison whenever the pair changes, the panel opens, OR a
  // segment was mutated elsewhere (refreshKey) — so edited chips / promotions
  // show live without a manual tab reload.
  useEffect(() => { if (open && a && b && a !== b) runCompare(); }, [open, a, b, runCompare, refreshKey]);

  const promote = async (seg) => {
    if (seg.is_primary) return;
    setPromoting(seg.id);
    try {
      await api.post(`${API}/api/brain/icp/segments/${seg.id}/activate`);
      toast.success(`"${seg.name}" is now your primary targeting`);
      await loadSegments();
      await runCompare();
      if (onChanged) await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not promote segment');
    } finally { setPromoting(null); }
  };

  if (segments.length < 2) return null;

  const sharedSets = {};
  if (result?.shared) {
    for (const k of Object.keys(result.shared)) {
      sharedSets[k] = new Set((result.shared[k] || []).map((v) => String(v).trim().toLowerCase()));
    }
  }
  const hasAnyShared = result?.shared && Object.values(result.shared).some((v) => (v || []).length > 0);

  return (
    <div className="rounded-xl border bg-card p-5 shadow-sm" data-testid="icp-segment-compare">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold text-foreground flex items-center gap-2">
            <GitCompare className="w-4 h-4 text-primary" /> Compare Segments
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            See two targeting segments side-by-side and promote the winner to primary.
          </p>
        </div>
        <button
          onClick={() => setOpen((o) => !o)}
          data-testid="icp-compare-toggle"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border hover:bg-muted transition-colors"
        >
          {open ? <><X className="w-3.5 h-3.5" /> Close</> : <><GitCompare className="w-3.5 h-3.5" /> Compare</>}
        </button>
      </div>

      {open && (
        <div className="mt-4" data-testid="icp-compare-panel">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mb-4">
            <select value={a} onChange={(e) => setA(e.target.value)} data-testid="icp-compare-select-a"
              className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
              {segments.map((s) => <option key={s.id} value={s.id} disabled={s.id === b}>{s.name}</option>)}
            </select>
            <span className="text-xs font-bold text-muted-foreground px-1 text-center">VS</span>
            <select value={b} onChange={(e) => setB(e.target.value)} data-testid="icp-compare-select-b"
              className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
              {segments.map((s) => <option key={s.id} value={s.id} disabled={s.id === a}>{s.name}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground" data-testid="icp-compare-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Measuring live reach…
            </div>
          ) : result && result.segments?.length === 2 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="icp-compare-cards">
                {result.segments.map((seg) => {
                  const isWinner = result.winner_id === seg.id;
                  const other = result.segments.find((s) => s.id !== seg.id);
                  const bd = seg.breakdown || {};
                  const obd = (other || {}).breakdown || {};
                  return (
                    <div key={seg.id}
                      className={`rounded-xl border p-4 transition-colors ${isWinner ? 'border-emerald-500/50 bg-emerald-500/[0.04]' : 'border-border bg-background/40'}`}
                      data-testid={`icp-compare-card-${seg.id}`}>
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="min-w-0">
                          <p className="font-semibold text-foreground truncate flex items-center gap-1.5">
                            {seg.is_primary && <Star className="w-3.5 h-3.5 text-primary fill-current shrink-0" />}
                            {seg.name}
                          </p>
                          <p className="text-[11px] text-muted-foreground truncate">{seg.summary}</p>
                        </div>
                        {isWinner && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 bg-emerald-500/15 px-2 py-0.5 rounded-full shrink-0" data-testid={`icp-compare-winner-${seg.id}`}>
                            <Trophy className="w-3 h-3" /> More reach
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-2 mb-3">
                        <Stat icon={Building2} value={bd.companies} label="Companies" testid={`icp-compare-companies-${seg.id}`}
                          best={bd.companies != null && (bd.companies || 0) >= (obd.companies || 0)} />
                        <Stat icon={Users2} value={bd.people} label="People" testid={`icp-compare-people-${seg.id}`}
                          best={bd.people != null && (bd.people || 0) >= (obd.people || 0)} />
                        <Stat icon={Mail} value={bd.contactable} label="Contactable" testid={`icp-compare-contactable-${seg.id}`}
                          best={bd.contactable != null && (bd.contactable || 0) >= (obd.contactable || 0)} />
                        <Stat icon={Phone} value={bd.with_phone} label="With phone" approx testid={`icp-compare-phone-${seg.id}`}
                          best={bd.with_phone != null && (bd.with_phone || 0) >= (obd.with_phone || 0)} />
                      </div>

                      <div className="space-y-2 mb-3">
                        <ChipList label="Titles" items={seg.chips.titles} sharedSet={sharedSets.titles} />
                        <ChipList label="Industries" items={seg.chips.industries} sharedSet={sharedSets.industries} />
                        <ChipList label="Locations" items={seg.chips.locations} sharedSet={sharedSets.locations} />
                        <ChipList label="Seniorities" items={seg.chips.seniorities} sharedSet={sharedSets.seniorities} />
                        <ChipList label="Company sizes" items={seg.chips.company_sizes} sharedSet={sharedSets.company_sizes} />
                        {seg.chips.keyword && <ChipList label="Keyword" items={[seg.chips.keyword]} />}
                      </div>

                      {seg.is_primary ? (
                        <div className="inline-flex items-center gap-1.5 text-xs font-medium text-primary px-3 py-1.5 rounded-lg bg-primary/10 w-full justify-center" data-testid={`icp-compare-isprimary-${seg.id}`}>
                          <Crown className="w-3.5 h-3.5" /> Current primary
                        </div>
                      ) : (
                        <button onClick={() => promote(seg)} disabled={promoting === seg.id}
                          data-testid={`icp-compare-promote-${seg.id}`}
                          className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 w-full justify-center transition-colors">
                          {promoting === seg.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Crown className="w-3.5 h-3.5" />}
                          Promote to primary
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 rounded-lg border border-sky-500/25 bg-sky-500/[0.05] px-4 py-3" data-testid="icp-compare-shared">
                <p className="text-xs font-medium text-sky-600 flex items-center gap-1.5 mb-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> Shared targeting
                </p>
                {hasAnyShared ? (
                  <div className="space-y-1.5">
                    {Object.keys(result.shared).map((k) => (
                      (result.shared[k] || []).length > 0 && (
                        <ChipList key={k} label={k.replace(/_/g, ' ')} items={result.shared[k]} />
                      )
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">These segments have no overlapping chips — they target distinct audiences.</p>
                )}
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground italic py-6 text-center" data-testid="icp-compare-pick">
              Pick two different segments above to compare their reach.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default ICPSegmentCompare;
